/* eslint-disable no-nested-ternary */
import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import DirectMessagesTable from '@company/components/CommunicationHub/CommunicationHubTables/DirectMessagesTable';
import JobMessagesTable from '@company/components/CommunicationHub/CommunicationHubTables/JobMessagesTable';
import ReceivedReviewsTable from '@company/components/CommunicationHub/CommunicationHubTables/ReceivedReviewsTable';
import ReviewRequestsTable from '@company/components/CommunicationHub/CommunicationHubTables/ReviewRequestsTable';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import { clearCompanyIndicatorsCache } from '@company/hooks/useCompanyIndicators';
import {
  dismissItemsFromNew,
  getDirectMessages,
  getJobChats,
  getPendingReviews,
  getReceivedReviews,
} from '@company/services/com-hub/api';
import {
  ComHubTabRoutes,
  ComHubTabs,
  CounterType,
  Review,
  ReviewMetadata,
  UserActionAssociatedTypeEnum,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import useAlert from '@hooks/useAlert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissItems,
  extractComHubUrlParams,
  getTabQueryParameter,
  handleComHubSearch,
  handleDismissOptimisticRollback,
  handleDismissOptimisticUpdate,
  invalidateQueriesForDismissedItems,
  isMessagingTab,
} from '@utils/ComHubUtils';
import { useCallback, useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import './styles.css';

interface NewsProps {
  onSeeReceivedReview: (review: Review) => void;
  onSeeReviewRequest: (review: ReviewMetadata) => void;
  onShowWorkerProfile: (data: WorkerModalParams) => void;
  jobChatsCount?: number;
  directMessagesCount?: number;
  onCounterUpdate: (type: CounterType, delta: number) => void;
}

const renderShowTabButton = (
  items: any[] | undefined,
  tabKey: string,
  tabLabel: string
) => {
  if (items && items?.length >= 10) {
    const paramName = getTabQueryParameter(tabKey);
    return (
      <Link to={`?tab=${tabKey}&${paramName}=true`} className="show-all-cta">
        Show All {isMessagingTab(tabKey) ? 'Unread' : 'New'} {tabLabel}
      </Link>
    );
  }
  return null;
};

function News({
  onSeeReceivedReview,
  onSeeReviewRequest,
  onShowWorkerProfile,
  jobChatsCount,
  directMessagesCount,
  onCounterUpdate,
}: NewsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [isInitialized, setIsInitialized] = useState(false);

  // CONSTANTS FOR QUERY
  const queryClient = useQueryClient();
  const { showErrorAlert } = useAlert();
  const queryKeyReceivedReviews = [
    QueryKeys.ComHub.RECEIVED_REVIEWS,
    searchState,
  ];
  const queryKeyReviewRequests = [
    QueryKeys.ComHub.REVIEW_REQUESTS,
    searchState,
  ];
  const queryKeyJobChats = [QueryKeys.ComHub.JOB_CHAT, searchState];
  const queryKeyDirectMessages = [QueryKeys.ComHub.DIRECT_CHAT, searchState];

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const { searchState: urlSearchState } =
      extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }

    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  // FETCH NEW RECEIVED REVIEWS
  const {
    data: newReceivedReviews,
    isLoading: isLoadingReceivedReviews,
    error: receivedReviewsError,
  } = useQuery({
    queryKey: queryKeyReceivedReviews,
    queryFn: () =>
      getReceivedReviews(
        0, // offset 0, no pagination
        10, // size of the first page (to se more it will redirect to the tab)
        searchState?.searchText,
        searchState?.dateRange,
        true
      ),
    enabled: isInitialized,
  });

  // FETCH NEW REVIEW REQUESTS
  const {
    data: newReviewRequests,
    isLoading: isLoadingReviewRequests,
    error: reviewRequestsError,
  } = useQuery({
    queryKey: queryKeyReviewRequests,
    queryFn: () =>
      getPendingReviews(
        0, // offset 0, no pagination
        10, // size of the first page (to se more it will redirect to the tab)
        searchState?.searchText,
        searchState?.dateRange,
        true
      ),
    enabled: isInitialized,
  });

  // FETCH NEW JOB MESSAGES
  const {
    data: jobChatsResponse,
    isLoading: isLoadingJobChats,
    error: jobChatsError,
  } = useQuery({
    queryKey: queryKeyJobChats,
    queryFn: () =>
      getJobChats(
        0, // offset 0, no pagination
        10, // size of the first page (to se more it will redirect to the tab)
        searchState?.searchText,
        searchState?.dateRange,
        true
      ),
    enabled: isInitialized, // Only run query after initialization
  });

  // FETCH NEW DIRECT MESSAGES
  const {
    data: directMessagesResponse,
    isLoading: isLoadingDirectMessages,
    error: directMessagesError,
  } = useQuery({
    queryKey: queryKeyDirectMessages,
    queryFn: () =>
      getDirectMessages(
        0, // offset 0, no pagination
        10, // size of the first page (to se more it will redirect to the tab)
        searchState?.searchText,
        searchState?.dateRange,
        true
      ),
    enabled: isInitialized, // Only run query after initialization
  });

  // Handle query errors
  useEffect(() => {
    if (receivedReviewsError) {
      showErrorAlert(
        receivedReviewsError.message ||
          'Failed to load received reviews. Please try again.'
      );
    }
  }, [receivedReviewsError, showErrorAlert]);

  useEffect(() => {
    if (reviewRequestsError) {
      showErrorAlert(
        reviewRequestsError.message ||
          'Failed to load review requests. Please try again.'
      );
    }
  }, [reviewRequestsError, showErrorAlert]);

  useEffect(() => {
    if (jobChatsError) {
      showErrorAlert(
        jobChatsError.message ||
          'Failed to load job messages. Please try again.'
      );
    }
  }, [jobChatsError, showErrorAlert]);

  useEffect(() => {
    if (directMessagesError) {
      showErrorAlert(
        directMessagesError.message ||
          'Failed to load direct messages. Please try again.'
      );
    }
  }, [directMessagesError, showErrorAlert]);

  // useQuery update
  const { mutateAsync, isPending: isLoadingDismiss } = useMutation({
    mutationFn: dismissItemsFromNew,
    onSuccess: (_, { items }) => {
      handleDismissOptimisticUpdate(queryClient, items, 'removeItems');
      clearCompanyIndicatorsCache();
    },
    onError: (_, { items }) => {
      handleDismissOptimisticRollback(queryClient, items, 'removeItems');
    },
    onSettled: (_, __, { items }) => {
      invalidateQueriesForDismissedItems(queryClient, items);
      // Update specific counter based on item type
      const associatedType = items[0]?.associatedType;
      const dismissedCount = items.reduce(
        (acc, item) => acc + item.associatedIds.length,
        0
      );
      if (associatedType === UserActionAssociatedTypeEnum.COMPANY_RATING) {
        onCounterUpdate(CounterType.RECEIVED_REVIEWS, -dismissedCount);
      } else if (
        associatedType === UserActionAssociatedTypeEnum.RATING_REQUEST
      ) {
        onCounterUpdate(CounterType.REVIEW_REQUESTS, -dismissedCount);
      }
    },
  });

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, updateSearchParams);
    },
    [updateSearchParams]
  );

  return (
    <div className="news-container">
      <FilterBar onSearch={handleOnSearch} />

      <hr className="mt-0" />

      {isLoadingDismiss && <ComHubSkeletonLoader />}

      <Stack gap={3}>
        {/* JOB MESSAGES BLOCK */}
        {isLoadingJobChats ? (
          <div className="job-messages-container">
            <strong>Job Messages</strong>
            <hr style={{ margin: '10px 0' }} />
            <ComHubSkeletonLoader />
          </div>
        ) : jobChatsResponse ? (
          <div className="job-messages-container">
            <JobMessagesTable
              title="Job Messages"
              jobChats={jobChatsResponse.jobChats}
              onShowWorkerProfile={onShowWorkerProfile}
              onCounterUpdate={(delta) =>
                onCounterUpdate(CounterType.JOB_CHATS, delta)
              }
              headerCount={jobChatsCount}
            />
            {renderShowTabButton(
              jobChatsResponse.jobChats,
              ComHubTabRoutes[ComHubTabs.JOB_MESSAGES].key,
              'Job Messages'
            )}
          </div>
        ) : null}

        {/* DIRECT MESSAGES BLOCK */}
        {isLoadingDirectMessages ? (
          <div className="direct-messages-container">
            <strong>Direct Messages</strong>
            <hr style={{ margin: '10px 0' }} />
            <ComHubSkeletonLoader />
          </div>
        ) : directMessagesResponse ? (
          <div className="direct-messages-container">
            <DirectMessagesTable
              title="Direct Messages"
              directMessages={directMessagesResponse.directChats}
              onShowWorkerProfile={(workerId) =>
                onShowWorkerProfile({ workerId })
              }
              onCounterUpdate={(delta) =>
                onCounterUpdate(CounterType.DIRECT_MESSAGES, delta)
              }
              headerCount={directMessagesCount}
            />
            {renderShowTabButton(
              directMessagesResponse.directChats,
              ComHubTabRoutes[ComHubTabs.DIRECT_MESSAGES].key,
              'Direct Messages'
            )}
          </div>
        ) : null}

        {/* NEW RECEIVED REVIEWS BLOCK */}
        {isLoadingReceivedReviews ? (
          <div className="received-reviews-container">
            <strong>Received Reviews</strong>
            <hr style={{ margin: '10px 0' }} />
            <ComHubSkeletonLoader />
          </div>
        ) : newReceivedReviews?.reviews ? (
          <div className="received-reviews-container">
            <ReceivedReviewsTable
              title="Received Reviews"
              receivedReviews={newReceivedReviews.reviews}
              onSeeReceivedReview={onSeeReceivedReview}
              onShowWorkerProfile={onShowWorkerProfile}
              onDismissItems={(items) =>
                dismissItems(
                  items,
                  UserActionAssociatedTypeEnum.COMPANY_RATING,
                  mutateAsync
                )
              }
            />
            {renderShowTabButton(
              newReceivedReviews.reviews,
              ComHubTabRoutes[ComHubTabs.RECEIVED_REVIEWS].key,
              'Received Reviews'
            )}
          </div>
        ) : null}

        {/* NEW REVIEWS REQUESTS BLOCK */}
        {isLoadingReviewRequests ? (
          <div className="review-requests-container">
            <strong>Review Requests</strong>
            <hr style={{ margin: '10px 0' }} />
            <ComHubSkeletonLoader />
          </div>
        ) : newReviewRequests?.ratingRequests ? (
          <div className="review-requests-container">
            <ReviewRequestsTable
              title="Review Requests"
              reviewRequests={newReviewRequests.ratingRequests}
              onSeeReviewRequest={onSeeReviewRequest}
              onShowWorkerProfile={onShowWorkerProfile}
              onDismissItems={(items) =>
                dismissItems(
                  items,
                  UserActionAssociatedTypeEnum.RATING_REQUEST,
                  mutateAsync
                )
              }
            />
            {renderShowTabButton(
              newReviewRequests.ratingRequests,
              ComHubTabRoutes[ComHubTabs.RECEIVED_REQUESTS].key,
              'Review Requests'
            )}
          </div>
        ) : null}
      </Stack>
    </div>
  );
}

export default News;
